import { useState, useEffect } from 'react'
import { RouteData, useAppStore, POI, Photo } from '@cya-routes/shared'
import { X, Search, MapPin, Mountain, Route, Download, Eye, Filter, Globe, Bike, TreePine, Footprints, Calendar, FileText, Share2, FolderOpen, Trash2, Tent, RotateCcw, Clock } from 'lucide-react'
import { listRoutes, loadRoute, deleteUnifiedRoute, UnifiedRoute } from '../../services/firebase/routes'
import { RouteDisplayPage } from '../display/RouteDisplayPage'

interface RouteLoadModalProps {
  isOpen: boolean
  onClose: () => void
  onLoadRoute: (route: RouteData | RouteData[], draftInfo?: { id: string, name: string, description: string, masterRouteDescription?: string, routeType?: string, hasMasterRoute?: boolean, pois?: POI[], photos?: Photo[], highlightImages?: { main?: string, additional?: string[] } }) => void
  refreshTrigger?: number // Optional trigger to force data refresh
}

interface LoadableRoute extends UnifiedRoute {
  // Additional computed fields for display
  totalDistance: number
  totalElevation: number
  location?: {
    country: string
    state: string
    city: string
    region: string
  }
  activityType?: 'cycling' | 'hiking' | 'mtb'
  terrainType?: 'road' | 'gravel' | 'mixed'
  surfaceAnalysis?: {
    roadPercentage: number
    gravelPercentage: number
    trailPercentage: number
  }
}

// Mock data removed - now using real Firebase data

const BADGE_COLORS = 'text-gray-300 bg-gray-700'

export function RouteLoadModal({ isOpen, onClose, onLoadRoute, refreshTrigger }: RouteLoadModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  // Removed selectedRoute - no longer needed for single column layout
  const [loadableRoutes, setLoadableRoutes] = useState<LoadableRoute[]>([])
  const [filteredRoutes, setFilteredRoutes] = useState<LoadableRoute[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState('all')
  const [selectedState, setSelectedState] = useState('all')
  const [selectedRegion, setSelectedRegion] = useState('all')
  const [selectedActivity, setSelectedActivity] = useState('all')
  // const [selectedTerrain, setSelectedTerrain] = useState('all') // Currently unused
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [deletingRouteId, setDeletingRouteId] = useState<string | null>(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewRoute, setPreviewRoute] = useState<LoadableRoute | null>(null)
  const [loadingRouteId, setLoadingRouteId] = useState<string | null>(null)
  const { auth } = useAppStore()

  // Load user's drafts and published routes from Firebase
  const loadUserData = async () => {
    if (!auth.isAuthenticated) {
      console.log('⚠️ loadUserData called but user not authenticated')
      return
    }
    
    // Check if auth.currentUser is properly initialized
    if (!auth.currentUser?.id) {
      console.log('⚠️ Auth user ID not available yet, retrying in 200ms...')
      setTimeout(() => loadUserData(), 200)
      return
    }
    
    console.log('🔄 loadUserData called, clearing existing data and loading fresh from Firebase')
    console.log('🔑 Current user ID:', auth.currentUser?.id)
    console.log('👤 Current user email:', auth.currentUser?.email)
    setIsLoading(true)
    try {
      // Load all user's routes using unified function
      console.log('📡 Fetching all routes from Firebase...')
      
      // First, let's check if there are ANY routes in the database WITHOUT any filters
      const allRoutesCheck = await listRoutes({
        // No filters at all - get everything
      })
      console.log('🌍 Total routes in database (NO FILTERS):', allRoutesCheck?.length || 0)
      if (allRoutesCheck && allRoutesCheck.length > 0) {
        console.log('🔍 First route owner ID:', allRoutesCheck[0].ownerId)
        console.log('🔍 First route data:', {
          id: allRoutesCheck[0].id,
          name: allRoutesCheck[0].name,
          type: allRoutesCheck[0].type,
          status: allRoutesCheck[0].status,
          ownerId: allRoutesCheck[0].ownerId
        })
        
        // Also check the auth user ID issue
        console.log('🚨 Auth state:', {
          isAuthenticated: auth.isAuthenticated,
          currentUser: auth.currentUser,
          id: auth.currentUser?.id,
          email: auth.currentUser?.email
        })
      }
      
      // Get current user ID
      const currentUserId = auth.currentUser?.id
      console.log('🔍 Current user ID:', currentUserId)
      console.log('🔍 Full auth state:', {
        id: auth.currentUser?.id,
        email: auth.currentUser?.email,
        firstName: auth.currentUser?.firstName,
        lastName: auth.currentUser?.lastName
      })
      
      // Also try getting ALL draft routes without owner filter to debug
      const allDraftRoutes = await listRoutes({
        type: 'draft'
        // No owner filter
      })
      console.log('📝 ALL draft routes in database:', allDraftRoutes?.length || 0)
      if (allDraftRoutes && allDraftRoutes.length > 0) {
        console.log('📝 First draft route owner:', allDraftRoutes[0].ownerId)
        console.log('📝 Does it match current user?', allDraftRoutes[0].ownerId === currentUserId)
      }
      
      // Now query for user's routes - get both published and draft for current user
      const publishedRoutes = await listRoutes({
        type: 'published', // Use type instead of status
        ownerId: currentUserId // Add owner filter
      })
      console.log('📚 Published routes for user:', publishedRoutes?.length || 0)
      
      // Try draft status too
      const draftRoutes = await listRoutes({
        type: 'draft', // Use type instead of status
        ownerId: currentUserId // Add owner filter
      })
      console.log('📝 Draft routes for user:', draftRoutes?.length || 0)
      
      // Combine them
      const allRoutes = [...(publishedRoutes || []), ...(draftRoutes || [])]
      console.log('📦 Received routes from Firebase (for current user):', allRoutes?.length || 0, 'routes')
      
      const loadableRoutes: LoadableRoute[] = []
      
      // Convert unified routes to LoadableRoute format
      if (allRoutes) {
        allRoutes.forEach(route => {
          // Skip autosaves - we only want to show drafts and published routes
          if (route.type === 'autosave') return
          
          // Calculate totals
          const totalDistance = route.distance || 0
          const totalElevation = route.elevation?.up || 0
          
          // Extract location from the stats-only segments (from listRoutes) or full routes
          let location: LoadableRoute['location'] = undefined
          
          // Check _statsOnlySegments first (from listRoutes)
          if ((route as any)._statsOnlySegments && (route as any)._statsOnlySegments.length > 0) {
            const firstSegmentLocation = (route as any)._statsOnlySegments[0].location
            if (firstSegmentLocation) {
              location = {
                country: firstSegmentLocation.country || '',
                state: firstSegmentLocation.state || '',
                city: firstSegmentLocation.city || '',
                region: firstSegmentLocation.region || firstSegmentLocation.city || ''
              }
            }
          } 
          // Fall back to full routes if available
          else if (route.routes && route.routes.length > 0 && route.routes[0].location) {
            const firstRouteLocation = route.routes[0].location
            location = {
              country: firstRouteLocation.country || '',
              state: firstRouteLocation.state || '',
              city: firstRouteLocation.city || '',
              region: firstRouteLocation.region || firstRouteLocation.city || ''
            }
          }

          loadableRoutes.push({
            ...route,
            totalDistance,
            totalElevation,
            location
          })
        })
      }
      
      
      // Sort by updated date, most recent first
      loadableRoutes.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      
      console.log('📋 Setting loadable routes:')
      loadableRoutes.forEach((r, i) => {
        console.log(`  Route ${i}: ${r.name} - routeType: ${r.routeType} (from DB)`)
      })
      
      // Also log the full thumbnail URLs
      loadableRoutes.forEach((route, index) => {
        console.log(`📸 Route ${index} (${route.name}) thumbnail:`, route.thumbnail || 'NO THUMBNAIL')
      })
      setLoadableRoutes(loadableRoutes)
    } catch (error) {
      console.error('Error loading user routes:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Load data when modal opens and user is authenticated, or when refresh is triggered
  useEffect(() => {
    console.log('🔄 RouteLoadModal useEffect triggered:', { isOpen, isAuthenticated: auth.isAuthenticated, refreshTrigger })
    if (isOpen && auth.isAuthenticated) {
      console.log('📥 Loading user data due to trigger change');
      // Use IIFE to handle async operation properly
      (async () => {
        // Add a small delay to ensure Firebase auth is fully initialized
        await new Promise(resolve => setTimeout(resolve, 100))
        await loadUserData()
      })()
    } else if (!isOpen) {
      // Reset states when modal closes
      console.log('🗑️ Clearing route data on modal close')
      setLoadableRoutes([])
      setFilteredRoutes([])
      setSearchTerm('')
      setSelectedType('all')
      setLoadingRouteId(null)
    }
  }, [isOpen, auth.isAuthenticated, refreshTrigger])

  // Filter routes based on search and type
  useEffect(() => {
    let filtered = loadableRoutes

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(route => 
        route.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        route.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filter by route type
    if (selectedType !== 'all') {
      filtered = filtered.filter(route => route.type === selectedType)
    }

    // Filter by location
    if (selectedCountry !== 'all') {
      filtered = filtered.filter(route => route.location?.country === selectedCountry)
    }
    if (selectedState !== 'all') {
      filtered = filtered.filter(route => route.location?.state === selectedState)
    }
    if (selectedRegion !== 'all') {
      filtered = filtered.filter(route => route.location?.region === selectedRegion)
    }

    // Filter by route type (bikepacking, single, etc.)
    if (selectedActivity !== 'all') {
      filtered = filtered.filter(route => {
        // Handle both underscore and space formats
        const normalizedRouteType = route.routeType?.replace(' ', '_')
        return normalizedRouteType === selectedActivity || route.routeType === selectedActivity
      })
    }

    console.log('🔍 Filtered routes for display:', {
      totalLoadableRoutes: loadableRoutes.length,
      filteredCount: filtered.length,
      searchTerm: searchTerm.trim(),
      selectedActivity,
      selectedType,
      routeTypes: loadableRoutes.map(r => r.routeType),
      filteredRouteNames: filtered.map(r => r.name)
    })
    setFilteredRoutes(filtered)
  }, [loadableRoutes, searchTerm, selectedType, selectedCountry, selectedState, selectedRegion, selectedActivity])

  // Get unique values for dropdowns
  const getUniqueCountries = () => {
    const countries = loadableRoutes
      .filter(route => route.location?.country)
      .map(route => route.location!.country)
    return [...new Set(countries)]
  }
  const getUniqueStates = () => {
    const routes = selectedCountry === 'all' ? loadableRoutes : 
      loadableRoutes.filter(route => route.location?.country === selectedCountry)
    const states = routes
      .filter(route => route.location?.state)
      .map(route => route.location!.state)
    return [...new Set(states)]
  }
  const getUniqueRegions = () => {
    let routes = loadableRoutes
    if (selectedCountry !== 'all') {
      routes = routes.filter(route => route.location?.country === selectedCountry)
    }
    if (selectedState !== 'all') {
      routes = routes.filter(route => route.location?.state === selectedState)
    }
    const regions = routes
      .filter(route => route.location?.region)
      .map(route => route.location!.region)
    return [...new Set(regions)]
  }

  const handleDeleteRoute = async (route: LoadableRoute, event: React.MouseEvent) => {
    event.stopPropagation() // Prevent triggering route selection
    
    if (!confirm(`Are you sure you want to delete "${route.name}"? This action cannot be undone.`)) {
      return
    }

    setDeletingRouteId(route.id)
    try {
      const success = await deleteUnifiedRoute(route.id)
      if (success) {
        console.log('✅ Route deleted successfully:', route.name)
        // Reload the data to refresh the list
        await loadUserData()
      } else {
        alert('Failed to delete route. Please try again.')
      }
    } catch (error) {
      console.error('Failed to delete route:', error)
      alert('Failed to delete route. Please try again.')
    } finally {
      setDeletingRouteId(null)
    }
  }

  const handleLoadRoute = async (route: LoadableRoute) => {
    setLoadingRouteId(route.id)
    try {
      // Log the route type from the list
      console.log('🎯 handleLoadRoute - route.routeType from list:', route.routeType)
      
      // If route data is not loaded (empty arrays), load full data
      if (route.routes.length === 0) {
        console.log('📦 Loading full route data for:', route.name)
        const fullRoute = await loadRoute(route.id)
        if (!fullRoute) {
          console.error('Failed to load full route data')
          alert('Failed to load route. Please try again.')
          return
        }
        
        console.log('📝 Full route loaded with description:', {
          id: fullRoute.id,
          name: fullRoute.name,
          description: fullRoute.description,
          masterRouteDescription: fullRoute.masterRouteDescription,
          routeType: fullRoute.routeType
        })
        
        // Update the route with full data
        route.routes = fullRoute.routes || []
        route.pois = fullRoute.pois || []
        route.photos = fullRoute.photos || []
        route.description = fullRoute.description || ''
        route.masterRouteDescription = fullRoute.masterRouteDescription || ''
        route.routeType = fullRoute.routeType || route.routeType // Ensure routeType is preserved
        route.logoUrl = fullRoute.logoUrl
        route.secondaryLogoUrl = fullRoute.secondaryLogoUrl
      }
      
      // Pass all routes as an array so the editor can load them as separate segments
      if (route.routes && route.routes.length > 0) {
        // Create route info for tracking with unified structure
        const routeInfo = {
          id: route.id,
          name: route.name,
          description: route.description || '',
          masterRouteDescription: route.masterRouteDescription,
          routeType: route.routeType,
          hasMasterRoute: route.hasMasterRoute || false,
          pois: route.pois || [],
          photos: route.photos || [],
          type: route.type, // Pass unified type
          status: route.status, // Pass unified status
          submission: route.submission || null, // Pass submission data for club pre-selection
          highlightImages: route.highlightImages, // Pass highlight images
          logoUrl: route.logoUrl, // Pass logo URL
          secondaryLogoUrl: route.secondaryLogoUrl, // Pass secondary logo URL
          attribution: route.attribution // Pass attribution data
        }
        
        console.log('📤 Passing route info to editor:', {
          id: routeInfo.id,
          name: routeInfo.name,
          description: routeInfo.description,
          masterRouteDescription: routeInfo.masterRouteDescription,
          routeType: routeInfo.routeType,
          type: routeInfo.type,
          status: routeInfo.status,
          submission: routeInfo.submission,
          highlightImages: routeInfo.highlightImages,
          logoUrl: routeInfo.logoUrl,
          secondaryLogoUrl: routeInfo.secondaryLogoUrl,
          attribution: routeInfo.attribution
        })

        if (route.routes.length === 1) {
          // Single route - pass as single RouteData object
          onLoadRoute(route.routes[0], routeInfo)
        } else {
          // Multiple routes - pass as array so they're loaded as separate segments
          onLoadRoute(route.routes, routeInfo)
        }
      }
      onClose()
    } finally {
      setLoadingRouteId(null)
    }
  }

  const handlePreviewRoute = async (route: LoadableRoute) => {
    // If route data is not loaded, load full data for preview
    if (route.routes.length === 0) {
      console.log('📦 Loading full route data for preview:', route.name)
      const fullRoute = await loadRoute(route.id)
      if (!fullRoute) {
        console.error('Failed to load full route data for preview')
        alert('Failed to load route. Please try again.')
        return
      }
      
      // Create a new route object with full data for preview
      const routeWithData = {...route}
      routeWithData.routes = fullRoute.routes || []
      routeWithData.pois = fullRoute.pois || []
      routeWithData.photos = fullRoute.photos || []
      setPreviewRoute(routeWithData)
    } else {
      setPreviewRoute(route)
    }
    setShowPreviewModal(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-AU')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" style={{ fontSize: 'min(1rem, 1.5vw)' }}>
      <div className="bg-gray-800/85 backdrop-blur-md rounded-xl shadow-2xl flex flex-col border border-gray-700/50" style={{ width: '80vw', maxWidth: '1000px', height: '85vh' }}>
        {/* Header */}
        <div className="border-b border-gray-700 flex items-center justify-between bg-gray-800/90 backdrop-blur-sm" style={{ padding: '1.5em' }}>
          <div className="flex items-center" style={{ gap: '1em' }}>
            <div className="bg-emerald-600 rounded-lg flex items-center justify-center" style={{ width: '3em', height: '3em' }}>
              <FolderOpen className="text-white" style={{ width: '1.5em', height: '1.5em' }} />
            </div>
            <div>
              <h2 className="font-bold text-white" style={{ fontSize: '1.5em' }}>Load Route</h2>
              <p className="text-gray-400" style={{ fontSize: '0.875em', marginTop: '0.25em' }}>Choose from your saved drafts and published routes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-gray-700 rounded-lg transition-colors"
            style={{ padding: '0.5em' }}
          >
            <X className="text-gray-400" style={{ width: '1.5em', height: '1.5em' }} />
          </button>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search and Filters */}
            <div className="border-b border-gray-700/50 bg-gray-800/50 backdrop-blur-sm" style={{ padding: '1em', display: 'flex', flexDirection: 'column', gap: '1em' }}>
              {/* Search */}
              <div className="relative">
                <Search className="absolute top-1/2 transform -translate-y-1/2 text-gray-400" style={{ left: '0.75em', width: '1em', height: '1em' }} />
                <input
                  type="text"
                  placeholder="Search routes, descriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-700/50 backdrop-blur-sm border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ paddingLeft: '2.5em', paddingRight: '1em', paddingTop: '0.5em', paddingBottom: '0.5em', fontSize: '1em' }}
                />
              </div>

              {/* Basic Filters Row */}
              <div className="grid grid-cols-2" style={{ gap: '0.75em' }}>
                {/* Route Types */}
                <select
                  value={selectedActivity}
                  onChange={(e) => setSelectedActivity(e.target.value)}
                  className="bg-gray-700/50 backdrop-blur-sm border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                >
                  <option value="all">All Types</option>
                  <option value="bikepacking">Bikepacking</option>
                  <option value="single">Single Route</option>
                  <option value="event">Event</option>
                  <option value="permanent">Permanent</option>
                  <option value="rail_trail">Rail Trail</option>
                  <option value="bike_path">Bike Path</option>
                </select>

                {/* Status */}
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="bg-gray-700/50 backdrop-blur-sm border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                >
                  <option value="all">All Status</option>
                  <option value="draft">Drafts</option>
                  <option value="submitted">Submitted</option>
                  <option value="published">Published</option>
                </select>
              </div>

              {/* Advanced Filters Toggle */}
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center text-gray-400 hover:text-gray-200 transition-colors"
                style={{ gap: '0.5em', fontSize: '0.875em' }}
              >
                <Filter style={{ width: '1em', height: '1em' }} />
                <span>Advanced Filters</span>
                <div className={`transform transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`}>
                  ▼
                </div>
              </button>

              {/* Advanced Filters */}
              {showAdvancedFilters && (
                <div className="pt-2 border-t border-gray-600" style={{ display: 'flex', flexDirection: 'column', gap: '0.75em' }}>
                  {/* Location Filters */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                    <div className="flex items-center text-gray-400" style={{ gap: '0.5em', fontSize: '0.75em' }}>
                      <Globe style={{ width: '0.75em', height: '0.75em' }} />
                      <span>Location</span>
                    </div>
                    
                    {/* Country */}
                    <select
                      value={selectedCountry}
                      onChange={(e) => {
                        setSelectedCountry(e.target.value)
                        setSelectedState('all')
                        setSelectedRegion('all')
                      }}
                      className="w-full bg-gray-700/50 backdrop-blur-sm border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    >
                      <option value="all">All Countries</option>
                      {getUniqueCountries().map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>

                    {/* State */}
                    <select
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value)
                        setSelectedRegion('all')
                      }}
                      disabled={selectedCountry === 'all'}
                      className="w-full bg-gray-700/50 backdrop-blur-sm border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                      style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    >
                      <option value="all">All States</option>
                      {getUniqueStates().map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>

                    {/* Region */}
                    <select
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      disabled={selectedState === 'all'}
                      className="w-full bg-gray-700/50 backdrop-blur-sm border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                      style={{ padding: '0.5em 0.75em', fontSize: '0.875em' }}
                    >
                      <option value="all">All Regions</option>
                      {getUniqueRegions().map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Route List - Full width single column */}
            <div className="flex-1 overflow-y-auto w-full">
              {(() => {
                console.log('🎨 Rendering route list:', {
                  isLoading,
                  filteredRoutesLength: filteredRoutes.length,
                  loadableRoutesLength: loadableRoutes.length
                })
                return null
              })()}
              {isLoading ? (
                <div className="p-8 text-center text-gray-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                  <p>Loading your routes...</p>
                </div>
              ) : filteredRoutes.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <Route className="mx-auto opacity-50" style={{ width: '3em', height: '3em', marginBottom: '1em' }} />
                  <p style={{ fontSize: '1em' }}>{loadableRoutes.length === 0 ? 'No saved routes found' : 'No routes found matching your criteria'}</p>
                  {loadableRoutes.length === 0 && (
                    <p style={{ fontSize: '0.75em', marginTop: '0.5em' }}>Save some drafts or publish routes to see them here</p>
                  )}
                </div>
              ) : (
                <div style={{ padding: '1.5em', display: 'flex', flexDirection: 'column', gap: '1em' }}>
                  {filteredRoutes.map((route) => (
                    <div
                      key={route.id}
                      className="rounded-lg border border-gray-600/50 bg-gray-700/40 hover:bg-gray-700/60 backdrop-blur-sm transition-all flex overflow-hidden"
                      style={{ height: '160px' }}
                    >
                      {/* Thumbnail */}
                      <div className="relative bg-gray-800" style={{ width: '240px', flexShrink: 0 }}>
                        {route.thumbnail ? (
                          <img
                            src={route.thumbnail}
                            alt={`Map preview of ${route.name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              target.nextElementSibling?.setAttribute('style', 'display: flex')
                            }}
                          />
                        ) : null}
                        <div className={`${route.thumbnail ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                          <MapPin className="text-gray-600" style={{ width: '2em', height: '2em' }} />
                        </div>
                        
                        {/* Route type badge overlay */}
                        {route.routeType && (
                          <span className="absolute top-2 left-2 px-2 py-1 rounded-md text-xs font-medium text-white bg-black/70 backdrop-blur-sm flex items-center gap-1">
                            {route.routeType === 'bikepacking' ? <Tent style={{ width: '0.75em', height: '0.75em' }} /> : 
                             route.routeType === 'single' ? <Route style={{ width: '0.75em', height: '0.75em' }} /> : 
                             route.routeType === 'event' ? <Calendar style={{ width: '0.75em', height: '0.75em' }} /> : 
                             (route.routeType === 'rail_trail' || route.routeType === 'rail trail') ? <TreePine style={{ width: '0.75em', height: '0.75em' }} /> :
                             (route.routeType === 'bike_path' || route.routeType === 'bike path') ? <Bike style={{ width: '0.75em', height: '0.75em' }} /> :
                             <RotateCcw style={{ width: '0.75em', height: '0.75em' }} />}
                            {route.routeType === 'single' ? 'SINGLE ROUTE' : 
                             (route.routeType === 'rail_trail' || route.routeType === 'rail trail') ? 'RAIL TRAIL' :
                             (route.routeType === 'bike_path' || route.routeType === 'bike path') ? 'BIKE PATH' : 
                             route.routeType.toUpperCase()}
                          </span>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 flex flex-col" style={{ padding: '1em' }}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-white truncate" style={{ fontSize: '1.125em' }}>{route.name}</h3>
                            <div className="flex items-center mt-1" style={{ gap: '0.5em' }}>
                              <span className={`rounded-md font-medium ${
                                route.type === 'draft' 
                                  ? 'text-blue-300 bg-blue-700' 
                                  : route.status === 'submitted'
                                  ? 'text-yellow-300 bg-yellow-700'
                                  : 'text-purple-200 bg-purple-600'
                              } flex items-center`} style={{ padding: '0.25em 0.5em', gap: '0.25em', fontSize: '0.75em' }}>
                                {route.type === 'draft' ? <FileText style={{ width: '0.75em', height: '0.75em' }} /> : 
                                 route.status === 'submitted' ? <Clock style={{ width: '0.75em', height: '0.75em' }} /> :
                                 <Share2 style={{ width: '0.75em', height: '0.75em' }} />}
                                {route.type === 'draft' ? 'DRAFT' : route.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex-1 flex flex-col justify-center" style={{ gap: '0.5em' }}>
                          <div className="flex items-center text-gray-300 flex-wrap" style={{ gap: '1em', fontSize: '0.875em' }}>
                            <div className="flex items-center" style={{ gap: '0.25em' }}>
                              <Route style={{ width: '1em', height: '1em' }} />
                              <span>{route.totalDistance.toFixed(1)} km</span>
                            </div>
                            <div className="flex items-center" style={{ gap: '0.25em' }}>
                              <Mountain style={{ width: '1em', height: '1em' }} />
                              <span>↑{formatNumber(route.totalElevation)}m</span>
                            </div>
                            <div className="flex items-center" style={{ gap: '0.25em' }}>
                              <span className="text-gray-400">{route.routeCount} route{route.routeCount !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          
                          {route.location && (
                            <div className="flex items-center text-gray-400" style={{ gap: '0.25em', fontSize: '0.875em' }}>
                              <MapPin style={{ width: '1em', height: '1em' }} />
                              <span className="truncate">{route.location.region}, {route.location.state}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center text-gray-400" style={{ gap: '0.25em', fontSize: '0.75em' }}>
                            <Calendar style={{ width: '0.875em', height: '0.875em' }} />
                            <span>{route.type === 'draft' ? 'Saved' : 'Published'} {formatDate(route.updatedAt.toISOString())}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col justify-center border-l border-gray-600" style={{ padding: '1em', gap: '0.5em' }}>
                        <button
                          onClick={() => handleLoadRoute(route)}
                          disabled={loadingRouteId === route.id}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                          style={{ padding: '0.5em 1em', gap: '0.5em', fontSize: '0.875em' }}
                        >
                          {loadingRouteId === route.id ? (
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <>
                              <Download style={{ width: '1em', height: '1em' }} />
                              Load
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handlePreviewRoute(route)}
                          className="bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                          style={{ padding: '0.5em 1em', gap: '0.5em', fontSize: '0.875em' }}
                        >
                          <Eye style={{ width: '1em', height: '1em' }} />
                          Preview
                        </button>
                        <button
                          onClick={(e) => handleDeleteRoute(route, e)}
                          disabled={deletingRouteId === route.id}
                          className="text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors flex items-center justify-center"
                          style={{ padding: '0.5em', fontSize: '0.875em' }}
                          title={`Delete ${route.name}`}
                        >
                          {deletingRouteId === route.id ? (
                            <div className="animate-spin border-2 border-red-400 border-t-transparent rounded-full" style={{ width: '1em', height: '1em' }} />
                          ) : (
                            <Trash2 style={{ width: '1em', height: '1em' }} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Route Preview Modal */}
      {showPreviewModal && previewRoute && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50">
          <RouteDisplayPage
            routes={previewRoute.routes}
            pois={previewRoute.pois}
            photos={previewRoute.photos}
            routeInfo={{
              name: previewRoute.name,
              description: previewRoute.description || '',
              routeType: previewRoute.routeType || 'Single Route',
              creator: 'You',
              createdAt: previewRoute.createdAt,
              club: undefined
            }}
            isPublic={false}
            showHeader={true}
            onClose={() => setShowPreviewModal(false)}
          />
        </div>
      )}
    </div>
  )
}